/* ===================================================================
   AdminTenantView -- OPTION A "operator views a client's full dashboard"
   (Liskov + Corbato, 2026-06-22). Route: /agency/tenant/:id

   Renders the REAL client Dashboard + Reviews components in a strictly
   READ-ONLY operator view, by mounting them inside <AdminViewProvider>. That
   provider flips every data hook to the admin-scoped, read-only endpoints
   (@require_admin on the Supabase user JWT) for THIS tenant, and makes every
   action hook inert. Nothing here can post / approve / edit a client's tenant
   -- and the real guarantee is server-side: there is NO admin write endpoint.

   GATING: like /agency, this calls getAdminMe() first. A non-admin (403/401)
   sees an "unauthorized" state and NO tenant data ever renders. Each load of
   the dashboard also writes an admin_audit_log/audit_log row server-side.

   This deliberately uses its OWN read-only chrome (not AppShell) so there is
   no global "Request reviews" / search / write affordance anywhere on screen.
   =================================================================== */
import { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { LogoutButton } from '../components/LogoutButton';
import { AdminViewProvider } from '../admin/AdminViewProvider';
import { Dashboard } from './Dashboard';
import { Reviews } from './Reviews';
import '../styles/shell.css';
import '../styles/components.css';

type ViewTab = 'dashboard' | 'reviews';

export function AdminTenantView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ViewTab>('dashboard');

  // 1. Gate: confirm the signed-in user is an operator (same query key the rest
  //    of the admin surface uses, so the cache is shared -- no extra request).
  const meQ = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => api.getAdminMe(signal),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const isAdmin = meQ.data?.is_admin === true;

  // 2. Tenant header label (business name) from the existing detail endpoint.
  //    Enabled only once admin-ness is confirmed.
  const tenantQ = useQuery({
    queryKey: ['admin', 'tenant', id],
    queryFn: ({ signal }) => api.getTenant(id as string, signal),
    enabled: Boolean(id) && isAdmin,
    staleTime: 30_000,
  });

  if (!id) return <Navigate to="/agency" replace />;

  if (meQ.isLoading) {
    return <CenterNote title="Loading..." body="Checking your admin access." />;
  }
  const unauthorized =
    meQ.isError &&
    meQ.error instanceof ApiError &&
    (meQ.error.status === 403 || meQ.error.status === 401);
  if (unauthorized || (meQ.isSuccess && !isAdmin)) {
    return (
      <CenterNote
        title="Not available"
        body="This area is for Rhody Reviews operators only. If you reached this by mistake, head back to your dashboard."
      />
    );
  }
  if (meQ.isError) {
    return (
      <CenterNote
        title="Could not load admin"
        body="There was a problem checking access. Please try again."
      />
    );
  }

  const businessName = tenantQ.data?.business_name || id;

  return (
    <div className="awrap" data-admin-readonly="true">
      {/* Operator top bar -- this is the platform operator console, read-only. */}
      <div className="wlbar">
        <span className="brandmark" style={{ width: 30, height: 30, borderRadius: 8 }}>
          <svg viewBox="0 0 16 16" fill="#fff" width={13} height={13}>
            <path d="M8 1.5l2 4.5 5 .5-3.7 3.4 1 4.9L8 12l-4.3 2.8 1-4.9L1 6.5 6 6z" />
          </svg>
        </span>
        <div className="nm">Rhody Reviews</div>
        <span className="wl">Operator console</span>
        <div className="me">
          <span className="role">Operator</span>
          <span className="ava">RR</span>
          <LogoutButton variant="bar" />
        </div>
      </div>

      {/* READ-ONLY banner -- unmissable, so the operator always knows they
          cannot act inside this client's tenant. */}
      <div className="ro-banner" role="status">
        <span className="ro-dot" aria-hidden="true" />
        <span>
          Read-only operator view of <strong>{businessName}</strong>. You can see this client's
          dashboard but cannot post, approve, edit, or send anything. Replies are approved by the
          business in its own dashboard.
        </span>
        <button className="mini mini--ghost" onClick={() => navigate('/agency')} style={{ marginLeft: 'auto' }}>
          Back to tenants
        </button>
      </div>

      {/* Dashboard / Reviews switch -- the two surfaces the operator can view. */}
      <div className="optabs" role="tablist" aria-label="Client views" style={{ marginTop: 14 }}>
        <button
          role="tab"
          aria-selected={tab === 'dashboard'}
          className={'optab' + (tab === 'dashboard' ? ' is-active' : '')}
          onClick={() => setTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          role="tab"
          aria-selected={tab === 'reviews'}
          className={'optab' + (tab === 'reviews' ? ' is-active' : '')}
          onClick={() => setTab('reviews')}
        >
          Reviews
        </button>
      </div>

      {/* The REAL client components, rendered read-only via the provider. */}
      <AdminViewProvider clientId={id}>
        <section className="view" style={{ paddingTop: 16 }}>
          {tab === 'dashboard' ? <Dashboard /> : <Reviews />}
        </section>
      </AdminViewProvider>
    </div>
  );
}

function CenterNote({ title, body }: { title: string; body: string }) {
  return (
    <div className="awrap">
      <div className="card" style={{ maxWidth: 520, margin: '64px auto', padding: 28, textAlign: 'center' }}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <p style={{ color: 'var(--ink-soft, #667)' }}>{body}</p>
      </div>
    </div>
  );
}
